/**
 * Analytics.jsx — Core analytics dashboard.
 *
 * Top KPIs:    Total Cost · Total Value · Profit/Loss · Items
 * Per-category Pie chart (slice = value share)
 * Per-collection table (items, cost, value, profit)
 *
 * No filters, no time-series, no AI. Reads existing item fields via the same
 * 3-tier fallback used elsewhere (dynamic field → top-level → legacy).
 */
import { useEffect, useMemo, useState } from "react";
/* eslint-disable react-hooks/exhaustive-deps */
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Loader2, TrendingUp, TrendingDown, Coins, Wallet, Boxes, ArrowLeft } from "lucide-react";
import { apiClient } from "@/lib/api";
import BottomNav from "@/components/BottomNav";
import { ChartFrame, ChartEmpty, CategoryPieChart } from "@/components/charts/Charts";
import { itemCost, itemValue, itemCostNZD, itemQty, computeItemValuation } from "@/utils/itemValue";
import { resolveBackHref } from "@/lib/navTracker";
import { fmt2 } from "@/lib/money";
import SalesHistoryTable from "@/components/SalesHistoryTable";
import { summariseSales } from "@/utils/salesTotals";

const money = (v) => `$${fmt2(v)}`;

const KPI = ({ label, value, hint, accent, Icon, testid, hintTestid, description }) => (
  <div
    className="vp-card p-5"
    data-testid={testid}
    title={description || (typeof value === "string" ? `${label}: ${value}` : label)}
    aria-label={description ? `${label} — ${description}` : label}
  >
    <div className="flex items-center justify-between mb-3">
      <span className="text-[10px] uppercase tracking-wider font-medium vp-muted">{label}</span>
      <span className="text-[hsl(var(--gold-strong))] dark:text-[hsl(var(--gold))]">
        <Icon size={16} strokeWidth={1.75} />
      </span>
    </div>
    <div className={`font-mono text-2xl font-semibold tracking-tight ${accent || ""}`}>
      {value}
    </div>
    {hint && <div className="text-xs vp-muted mt-1" data-testid={hintTestid}>{hint}</div>}
  </div>
);

export default function Analytics() {
  const navigate = useNavigate();
  const location = useLocation();
  const backHref = resolveBackHref({
    stateFrom: location.state?.from,
    currentPath: location.pathname + location.search,
    fallback: "/",
  });
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [collections, setCollections] = useState([]);
  const [sales, setSales] = useState([]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [itRes, catRes, colRes, salesRes] = await Promise.all([
          apiClient.get("/items"),
          apiClient.get("/categories"),
          apiClient.get("/collections"),
          apiClient.get("/sales"),
        ]);
        setItems(itRes.data || []);
        setCategories(catRes.data || []);
        setCollections(colRes.data || []);
        setSales(salesRes.data || []);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Ex-GST + NZD-normalised money helpers used by the Collections Snapshot
  // + Category pie (Portfolio Analytics only). Local to this component —
  // the shared `itemCost` / `itemValue` defaults to inc-GST and is
  // consumed by other screens, so we deliberately do NOT touch the shared
  // utility here. Cross-currency items are converted to NZD via
  // fx_rate_purchase (cost) and fx_rate_sale || fx_rate_purchase (value).
  const _fx = (v, fallback = 1) => {
    const n = Number(v);
    return isFinite(n) && n > 0 ? n : fallback;
  };
  // Dynamic tax rate resolver — mirrors utils/itemValue._itemTaxRate. Reads
  // the item's stored `tax_percent` when present; falls back to the global
  // `cdi.settings.taxPercent`. Replaces the legacy hard-coded 1.15 divisor.
  const _globalTaxPct = (() => {
    try {
      const raw = typeof window !== "undefined"
        ? window.localStorage.getItem("cdi.settings.taxPercent")
        : null;
      const n = Number(raw);
      return Number.isFinite(n) && n >= 0 ? n : 0;
    } catch { return 0; }
  })();
  const _itemTaxMultiplier = (it) => {
    const stored = it?.tax_percent;
    const hasStored = stored !== null && stored !== undefined && stored !== "";
    const n = hasStored ? Number(stored) : NaN;
    const pct = Number.isFinite(n) && n >= 0 ? n : _globalTaxPct;
    return 1 + (pct / 100);
  };
  const itemCostEx = (it) => {
    const ex = Number(it?.cost_price_ex_gst || 0);
    let base;
    if (ex) base = ex;
    else {
      const inc = Number(it?.cost_price_inc_gst || 0);
      base = inc ? inc / _itemTaxMultiplier(it) : itemCost(it);
    }
    const ccy = String(it?.currency_code || "NZD").toUpperCase();
    return ccy === "NZD" ? base : base * _fx(it?.fx_rate_purchase, 1);
  };
  const itemValueEx = (it) => {
    const ex = Number(it?.estimated_value_ex_gst || 0);
    let base;
    if (ex) base = ex;
    else {
      const inc = Number(it?.estimated_value_inc_gst || 0);
      base = inc ? inc / _itemTaxMultiplier(it) : 0;
    }
    const ccy = String(it?.currency_code || "NZD").toUpperCase();
    if (ccy === "NZD") return base;
    const fx = _fx(it?.fx_rate_sale, 0) || _fx(it?.fx_rate_purchase, 1);
    return base * fx;
  };

  // Pre-compute per-item valuation using the industry-standard formula
  // (2026-02 — mirrors backend `_compute_item_valuation`). Sales are
  // grouped by `item_id` up-front so every downstream reducer reads
  // pre-computed totals.
  const salesByItem = useMemo(() => {
    const map = new Map();
    for (const s of sales) {
      const k = s.item_id;
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(s);
    }
    return map;
  }, [sales]);
  const valuations = useMemo(() => {
    const map = new Map();
    for (const it of items) {
      map.set(it.id, computeItemValuation(it, salesByItem.get(it.id) || []));
    }
    return map;
  }, [items, salesByItem]);
  const _val = (it) => valuations.get(it?.id) || { current_value: 0, total_cost: 0, total_PL: 0, realised_PL: 0, unrealised_PL: 0 };

  const totals = useMemo(() => {
    let cost = 0, value = 0, inferredCount = 0, bottles = 0, totalPL = 0, costValue = 0, valuedCount = 0;
    for (const it of items) {
      const v = _val(it);
      cost += v.total_cost;
      // True cost basis — sums itemCostNZD across every item regardless
      // of valuation status. Independent from `cost` above and from the
      // includeInValuationTotals gate applied to value / totalPL.
      costValue += Number(v?.total_cost) || 0;
      if (v.includeInValuationTotals) {
        value += v.current_value;
        totalPL += v.total_PL;
        valuedCount += 1;
      } else {
        inferredCount += 1;
      }
      bottles += itemQty(it);
    }
    const pct = costValue > 0 ? (totalPL / costValue) * 100 : 0;
    // 2026-02-26 — Portfolio Profit / Loss is now driven by
    // `total_PL` (realised + unrealised ex-GST) rather than a naive
    // `value − cost` subtraction. `pct` follows the same source so
    // the KPI hint stays consistent with the value shown.
    return { cost, costValue, value, totalPL, pct, count: bottles, inferredCount, valuedCount };
  }, [items, valuations]);

  // Realised-side rollup. `total_PL` already includes realised sales,
  // so the "Total Performance" KPI reads directly from `totals.totalPL`
  // (no double-count with `salesSummary.realisedEx`).
  const salesSummary = useMemo(() => summariseSales(sales), [sales]);
  const combinedPerformance = totals.totalPL;

  // Group items by category for the pie chart (slice = total estimated
  // value, ex GST — consistent with the Collections Snapshot section).
  const categoryBreakdown = useMemo(() => {
    const byCat = new Map();
    for (const it of items) {
      const key = it.category_id || "__uncat__";
      const prev = byCat.get(key) || { id: key, cost: 0, value: 0, count: 0 };
      const v = _val(it);
      prev.cost += v.total_cost;
      if (v.includeInValuationTotals) {
        prev.value += v.current_value;
      }
      prev.count += 1;
      byCat.set(key, prev);
    }
    const out = [];
    for (const [id, row] of byCat) {
      const cat = categories.find((c) => c.id === id);
      out.push({
        name: cat?.category_name || (id === "__uncat__" ? "Uncategorised" : "Unknown"),
        value: Math.round(row.value),
        cost: Math.round(row.cost),
        count: row.count,
      });
    }
    return out.sort((a, b) => b.value - a.value);
  }, [items, categories]);

  // Per-collection summary rows — ex-GST values (matches the snapshot UI).
  // Seed the map with EVERY collection first (zeroed) so empty collections
  // still appear in the Snapshot list. Items then top-up their buckets;
  // any items whose `collection_id` no longer resolves fall into the
  // "__none__" bucket which is added lazily so we don't show an empty
  // "(No collection)" row when the portfolio is clean.
  const collectionRows = useMemo(() => {
    const byCol = new Map();
    for (const c of collections) {
      byCol.set(c.id, { id: c.id, cost: 0, value: 0, count: 0, collection_cost_value: 0, totalPL: 0, valuedCount: 0 });
    }
    for (const it of items) {
      const key = it.collection_id && byCol.has(it.collection_id)
        ? it.collection_id
        : "__none__";
      const prev = byCol.get(key) || { id: key, cost: 0, value: 0, count: 0, collection_cost_value: 0, totalPL: 0, valuedCount: 0 };
      const v = _val(it);
      prev.cost  += v.total_cost;
      // True cost basis — every item contributes, regardless of valuation
      // status. Independent from `cost` (ex-GST valuation basis) and
      // `value` above so it can never be masked by a missing valuation.
      prev.collection_cost_value += Number(v?.total_cost) || 0;
      if (v.includeInValuationTotals) {
        prev.value += v.current_value;
        prev.totalPL += v.total_PL;
        prev.valuedCount += 1;
      }
      prev.count += itemQty(it);
      byCol.set(key, prev);
    }
    const out = [];
    for (const [id, row] of byCol) {
      const col = collections.find((c) => c.id === id);
      const profit = row.valuedCount > 0 ? row.totalPL : 0;
      const pct = row.valuedCount > 0 && row.cost > 0 ? (row.totalPL / row.cost) * 100 : 0;
      out.push({
        id,
        name: col?.name || "(No collection)",
        ...row,
        profit,
        pct,
      });
    }
    return out.sort((a, b) => b.value - a.value);
  }, [items, collections]);

  const accentClass = totals.totalPL > 0
    ? "text-[hsl(var(--profit))]"
    : totals.totalPL < 0 ? "text-[hsl(var(--loss))]" : "vp-muted";

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10 sm:py-14" data-testid="analytics">
      <button
        type="button"
        onClick={() => navigate(backHref)}
        data-testid="analytics-back"
        aria-label="Back to previous screen"
        className="vp-btn vp-btn-secondary vp-btn-sm mb-4 inline-flex items-center gap-2"
      >
        <ArrowLeft size={14} />
        <span>Back</span>
      </button>
      <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 flex-wrap text-xs mb-6" data-testid="an-breadcrumb">
        <Link
          to="/"
          data-testid="an-breadcrumb-home"
          className="text-[10px] uppercase tracking-wider font-medium vp-muted px-2 py-0.5 rounded-full border border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))] hover:text-[hsl(var(--gold-strong))] dark:hover:text-[hsl(var(--gold))] transition-colors duration-fast"
        >
          Home
        </Link>
        <span className="vp-muted">→</span>
        <span data-testid="an-breadcrumb-current" className="text-xs font-medium">Portfolio Analytics</span>
      </nav>

      <header className="mb-8">
        <p className="text-xs uppercase tracking-wider vp-muted mb-1">Portfolio</p>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight" data-testid="an-title">Portfolio Analytics</h1>
        <p className="vp-muted text-sm mt-2">Snapshot of your entire portfolio across every collection.</p>
      </header>

      {loading ? (
        <div className="flex items-center gap-2 text-sm vp-muted py-12 justify-center">
          <Loader2 size={16} className="animate-spin" /> Loading…
        </div>
      ) : (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
            <KPI testid="an-kpi-cost"   label="Total Cost (ex GST)"   value={money(totals.costValue)}  Icon={Coins}
              description="True cost basis — sum of itemCostNZD across every item in your portfolio, regardless of valuation status." />
            <KPI
              testid="an-kpi-value"
              label="Estimated Value (valued items only)"
              value={money(totals.value)}
              Icon={Wallet}
              description="Current estimated value of every bottle you still own (ex GST). Uses Manual → Market → most recent sale per item. Items without a valuation are excluded from Market Value and Profit calculations but still contribute to Total Cost."
              hint={totals.inferredCount > 0
                ? `Excludes ${totals.inferredCount} item${totals.inferredCount === 1 ? "" : "s"} with no valuation set`
                : undefined}
              hintTestid={totals.inferredCount > 0 ? "id-pricing-value-inferred" : undefined}
            />
            <KPI
              testid="an-kpi-profit"
              label={totals.valuedCount === 0
                ? "Awaiting Valuation"
                : (totals.totalPL >= 0 ? "Profit (Realised + Unrealised)" : "Loss (Realised + Unrealised)")}
              value={totals.valuedCount === 0 ? "—" : money(totals.totalPL)}
              hint={totals.valuedCount === 0
                ? undefined
                : `${totals.pct >= 0 ? "+" : ""}${totals.pct.toFixed(1)}%`}
              accent={totals.valuedCount === 0 ? undefined : accentClass}
              Icon={totals.valuedCount === 0
                ? Coins
                : (totals.totalPL >= 0 ? TrendingUp : TrendingDown)}
              description={totals.valuedCount === 0
                ? "No items have a valuation set. Profit/Loss will appear once at least one item has a market value."
                : "Total P/L (realised + unrealised, ex-GST) across every valued item. Uses each item's `total_PL` so the KPI matches Collection Overview and the shared aggregators. Items without a valuation are excluded from P/L but still contribute to Total Cost."}
            />
            <KPI testid="an-kpi-items" label="Items"        value={String(totals.count)} Icon={Boxes}
              description="Number of distinct item records across every collection in your portfolio." />
          </div>

          {/* Category pie — labelled legend shows "Name — $value (X%)". */}
          <div className="mb-8">
            <ChartFrame title="Value by Category" subtitle="Each slice represents a category's share of total estimated value · Category totals shown ex GST" testid="an-category-pie" height={440}>
              {categoryBreakdown.length === 0
                ? <ChartEmpty message="No items yet." />
                : (
                  <CategoryPieChart
                    data={categoryBreakdown}
                    testid="an-category-pie"
                    legendFormatter={(name, payload, total) => {
                      const v = Number(payload?.value || 0);
                      const pct = total > 0 ? (v / total) * 100 : 0;
                      return `${name} — ${money(v)} (${pct.toFixed(1)}%)`;
                    }}
                  />
                )}
            </ChartFrame>
          </div>

          {/* Collections Snapshot — explicit ex-GST values per collection.
              Mobile: stacked rows with labelled key/value pairs (no more
              unlabelled number strip). Desktop: tabular row with sticky
              column header. Tapping a row opens that collection. */}
          <section className="vp-card p-6 mb-8" data-testid="an-collections-snapshot">
            <h2 className="text-lg font-medium tracking-tight mb-1">Collections Snapshot</h2>
            <p className="text-xs vp-muted mb-4">Items, cost, estimated value, profit / loss and profit % per collection. All values are ex-GST.</p>
            {collectionRows.length === 0 ? (
              <p className="text-sm vp-muted py-8 text-center">No items yet.</p>
            ) : (
              <div className="space-y-2">
                {/* Desktop column header */}
                <div className="hidden sm:grid grid-cols-12 gap-2 px-3 text-[10px] uppercase tracking-wider vp-muted">
                  <div className="col-span-3">Collection</div>
                  <div className="col-span-1 text-right">Items</div>
                  <div className="col-span-2 text-right">Cost (ex GST)</div>
                  <div className="col-span-2 text-right">Est. Value (ex GST)</div>
                  <div className="col-span-2 text-right">P/L (ex GST)</div>
                  <div className="col-span-2 text-right">Profit %</div>
                </div>
                {collectionRows.map((r) => {
                  const pl = r.profit;
                  const plClass = pl > 0 ? "text-[hsl(var(--profit))]" : pl < 0 ? "text-[hsl(var(--loss))]" : "vp-muted";
                  const pctClass = r.pct > 0 ? "text-[hsl(var(--profit))]" : r.pct < 0 ? "text-[hsl(var(--loss))]" : "vp-muted";
                  const pctStr = `${r.pct >= 0 ? "+" : ""}${r.pct.toFixed(1)}%`;
                  const desktopRow = (
                    <>
                      <div className="hidden sm:block col-span-3 font-medium text-sm truncate">{r.name}</div>
                      <div className="hidden sm:block col-span-1 text-sm font-mono text-right">{r.count}</div>
                      <div className="hidden sm:block col-span-2 text-sm font-mono text-right">{money(r.cost)}</div>
                      <div className="hidden sm:block col-span-2 text-sm font-mono text-right">{money(r.value)}</div>
                      <div className={`hidden sm:block col-span-2 text-sm font-mono text-right font-medium ${plClass}`}>{money(pl)}</div>
                      <div className={`hidden sm:block col-span-2 text-sm font-mono text-right font-medium ${pctClass}`} data-testid={`an-snapshot-pct-${r.id}`}>{pctStr}</div>
                    </>
                  );
                  // Mobile layout: name as header + 5 labelled rows.
                  const mobileRow = (
                    <div className="sm:hidden col-span-12">
                      <div className="font-medium text-sm mb-2 truncate" data-testid={`an-snapshot-name-${r.id}`}>{r.name}</div>
                      <div className="grid grid-cols-2 gap-y-1 gap-x-3 text-xs">
                        <span className="vp-muted">Items</span>
                        <span className="font-mono text-right">{r.count}</span>
                        <span className="vp-muted">Cost (ex GST)</span>
                        <span className="font-mono text-right">{money(r.cost)}</span>
                        <span className="vp-muted">Est. Value (ex GST)</span>
                        <span className="font-mono text-right">{money(r.value)}</span>
                        <span className="vp-muted">P/L (ex GST)</span>
                        <span className={`font-mono text-right font-medium ${plClass}`}>{money(pl)}</span>
                        <span className="vp-muted">Profit %</span>
                        <span className={`font-mono text-right font-medium ${pctClass}`}>{pctStr}</span>
                      </div>
                    </div>
                  );
                  const inner = (<>{desktopRow}{mobileRow}</>);
                  return r.id !== "__none__" ? (
                    <Link
                      key={r.id}
                      to={`/collections/${r.id}`}
                      data-testid={`an-snapshot-row-${r.id}`}
                      className="grid grid-cols-12 gap-2 items-center px-3 py-3 rounded-md border border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))] transition-colors duration-fast"
                    >
                      {inner}
                    </Link>
                  ) : (
                    <div
                      key={r.id}
                      data-testid="an-snapshot-row-none"
                      className="grid grid-cols-12 gap-2 items-center px-3 py-3 rounded-md border border-dashed border-[hsl(var(--border))]"
                    >
                      {inner}
                    </div>
                  );
                })}
              </div>
            )}
          </section>
          {/* Realised sales KPIs — mirrors the Collection KPI block on
              CollectionDetail. Values are absolute NZD-scaled totals of
              every sale returned by GET /api/sales. */}
          <section className="mb-8" data-testid="an-realised-kpis">
            <h2 className="text-lg font-medium tracking-tight mb-3">Realised Performance</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <KPI testid="an-kpi-realised-ex"
                label="Realised Profit (ex GST)"
                value={money(salesSummary.realisedEx)}
                accent={salesSummary.realisedEx >= 0 ? "text-[hsl(var(--profit))]" : "text-[hsl(var(--loss))]"}
                Icon={salesSummary.realisedEx >= 0 ? TrendingUp : TrendingDown}
                description="Actual profit realised on bottles you've already sold across all collections (ex GST): Σ (sale price − cost basis) × quantity sold." />
              <KPI testid="an-kpi-realised-inc"
                label="Realised Profit (inc GST)"
                value={money(salesSummary.realisedInc)}
                Icon={Coins}
                description="Same as Realised Profit but with GST included on sale proceeds and cost basis." />
              <KPI testid="an-kpi-qty-sold"
                label="Total Quantity Sold"
                value={String(Math.trunc(salesSummary.qtySold))}
                Icon={Boxes}
                description="Sum of bottles moved out via every recorded sale across all collections." />
              <KPI testid="an-kpi-sale-value"
                label="Total Sale Value (ex GST)"
                value={money(salesSummary.saleValueEx)}
                Icon={Wallet}
                description="Gross sale proceeds ex GST across every recorded sale in the portfolio." />
              <KPI testid="an-kpi-remaining-value"
                label="Remaining Quantity Value"
                value={money(totals.value)}
                Icon={Wallet}
                description="Current estimated value of the bottles you still own after all recorded sales." />
              <KPI testid="an-kpi-total-performance"
                label={combinedPerformance >= 0 ? "Total Performance (Realised + Unrealised)" : "Total Loss (Realised + Unrealised)"}
                value={money(combinedPerformance)}
                accent={combinedPerformance >= 0 ? "text-[hsl(var(--profit))]" : "text-[hsl(var(--loss))]"}
                Icon={combinedPerformance >= 0 ? TrendingUp : TrendingDown}
                description="Realised profit + unrealised gain — the full picture of how the portfolio has performed to date." />
            </div>
          </section>

          {/* Sales History — every sale across every collection. */}
          <section className="mb-8" data-testid="an-sales-history">
            <h2 className="text-lg font-medium tracking-tight mb-3">Sales History</h2>
            <SalesHistoryTable sales={sales} testid="an-sales-history-table" />
          </section>
        </>
      )}

      <BottomNav current="home" />
    </div>
  );
}
